/**
 * The firms a PCO application is filed under.
 *
 * Unlike DSA, a PCO application carries no counter of its own — every one of
 * them is filed from the same head office, under one of these five firm names.
 * The PAN form prints the firm on the office address's first line (see
 * PanFormDialog), and the Employer Certificate prints that firm's issuer
 * details and seal & signature (edited on the "PCO Firm Details" screen,
 * stored by the API's pco-firm module).
 *
 * The API seeds the same five names in the same order (pco-firm.service.ts).
 * Do not reorder: `pcoFirmFor` hashes over this order, so a reorder moves
 * existing applications to a different firm.
 */
export const PCO_FIRM_NAMES = [
  "YADAV ENTERPRISES",
  "LIKE ENTERPRISES",
  "AHIRWAL ENTERPRISES",
  "CHETAN ENTERPRISES",
  "BALAJI ENTERPRISES",
];

/**
 * Which of the five firms this application is filed under.
 *
 * The pick is spread across the list rather than being the same firm every
 * time, and it is derived from the application itself rather than drawn fresh
 * — `Math.random()` would hand the same application a different firm every
 * time the dialog is re-opened, so a form re-generated after a correction
 * would name a firm the first print never did. Hashing the SRN keeps one
 * application on one firm for good while still dealing the five out evenly
 * across applications.
 */
export const pcoFirmFor = (key: string) => {
  const seed = (key || "").trim();
  if (!seed) return PCO_FIRM_NAMES[0];
  let hash = 0;
  for (let i = 0; i < seed.length; i += 1) {
    hash = (hash * 31 + seed.charCodeAt(i)) % 100000007;
  }
  return PCO_FIRM_NAMES[hash % PCO_FIRM_NAMES.length];
};

/** A firm as GET /pco-firm/get-all returns it. */
export type PcoFirm = {
  _id: string;
  firmName: string;
  order: number;
  fullName: string;
  designation: string;
  panNumber: string;
  officeAddressWithLocation: string;
  telephone: string;
  mobile: string;
  sealSignatureImage: string;
  /** Blank letterhead (logo + address printed); the certificate is printed on it. */
  letterheadImage?: string;
};

const squash = (value?: string | null) =>
  (value || "").toUpperCase().replace(/[^A-Z0-9]/g, "");

const isPcoApplication = (data: any) =>
  data?.baseCatAppied === "CAT_PCO" ||
  (Array.isArray(data?.paymentCategory) &&
    data.paymentCategory.some((item: any) =>
      typeof item === "string" ? item === "CAT_PCO" : item?.code === "CAT_PCO"
    ));

/**
 * The firm the certificate should name, or null when this is not a PCO
 * certificate.
 *
 * The office address the PAN form printed comes first: it carries the firm
 * name on its first line, including when the operator typed over the rotation
 * (punctuation / line breaks are ignored, so "BALAJI, ENTERPRISES" still
 * matches). Only a PCO application with no matching office address falls back
 * to the same SRN hash the PAN form uses.
 */
export const findPcoFirm = (
  firms: PcoFirm[] | undefined | null,
  officeAddress: string | undefined,
  data: any
): PcoFirm | null => {
  if (!firms?.length) return null;
  const address = squash(officeAddress);
  if (address) {
    const hit = firms.find(
      (firm) => firm.firmName && address.includes(squash(firm.firmName))
    );
    if (hit) return hit;
  }
  if (!isPcoApplication(data)) return null;
  const name = pcoFirmFor(data?.srn || data?._id || data?.name);
  return firms.find((firm) => firm.firmName === name) || null;
};
